import React, { useEffect, useState } from 'react'
import { Box, Card, Divider, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import Header, { HeaderVariant } from '../../../Elements/Header'
import CardFormHeader from '../../../Card/CardFormHeader'
import ContainedButton, {
  ContainedButtonVariant,
} from '../../../Buttons/ContainedButton'
import {
  CashAmount,
  CommunityDirectorLicensingBilling,
  FetchLcdTotalOwedResponse,
  paymentsApi,
} from '../../../../api/swagger'
import { useLoadingIds } from '../../../../hooks/useLoadingIds'
import useLoadingContext from '../../../../hooks/useLoadingContext'
import { useAppSnackbar } from '../../../../hooks/useAppSnackbar'
import getLocaleCurrencyForAmount from '../../../../utils/getLocaleCurrencyForAmount'
import { convertTwoDecimalStringToBig } from '../../../../utils/bigJSHelper'
import PaymentModal from '../../../Modals/PaymentModal'
import ActionButtons from '../../../Buttons/ActionButtons'
import BasicModal from '../../../Modals/BasicModal'
import { useAuth } from '../../../Routes/AuthProvider'
import { colors } from '../../../../stylesheet'
import { PaymentModalVariant } from '../../../Modals/PaymentModal/types'
import { BillingFilters } from '../interfaces'

interface LCDHeaderProps {
  achAllowed: boolean
  achDiscount: string
  programs: Array<CommunityDirectorLicensingBilling>
  filters?: BillingFilters
}

const LCDHeader: React.FC<LCDHeaderProps> = ({
  achAllowed,
  achDiscount,
  programs,
  filters,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const { userDetails } = useAuth()
  const { handleError } = useAppSnackbar()
  const {
    BillingTab: {
      fetchBilling: fetchBillingLoadingId,
      fetchLicensedDirectorBillingDetails:
        fetchLicensedDirectorBillingDetailsLoadingId,
    },
    PaymentApi: {
      fetchLcdTotalOwed: fetchLcdTotalOwedLoadingId,
      fetchLicensingLcdPayments: fetchLicensingLcdPaymentsLoadingId,
    },
  } = useLoadingIds()
  const [lcdTotal, setLcdTotals] = useState<FetchLcdTotalOwedResponse>()
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [isAchNotAllowedModalOpen, setIsAchNotAllowedModalOpen] =
    useState(false)

  const fetchLcdTotalOwed = async () => {
    try {
      const result = await paymentsApi.fetchLcdTotalOwed()
      setLcdTotals(result)
    } catch (e) {
      await handleError(
        e,
        t(
          'LCDPaymentHeader.ValidationMessage.LcdTotalOwedFetchingError',
          'Something went wrong while fetching the total balance and due.'
        )
      )
    }
  }

  const { triggerFetch } = useLoadingContext({
    asyncFunction: fetchLcdTotalOwed,
    loadingId: fetchLcdTotalOwedLoadingId,
  })

  useEffect(() => {
    triggerFetch()
  }, [triggerFetch])

  const totalBalance = getLocaleCurrencyForAmount(
    lcdTotal?.totalBalance.amount ?? '0.00',
    lcdTotal?.totalBalance.currencyCode ?? 'USD'
  )
  const totalDue = getLocaleCurrencyForAmount(
    lcdTotal?.totalDue.amount ?? '0.00',
    lcdTotal?.totalDue.currencyCode ?? 'USD'
  )
  return (
    <>
      <PaymentModal
        isLcd
        loadingIdsToAddOnComplete={[
          fetchLcdTotalOwedLoadingId, // refresh total data shown in header
          fetchBillingLoadingId + JSON.stringify(filters), // refresh lcd table data
          fetchLicensingLcdPaymentsLoadingId, // refresh licensing history table data
          fetchLicensedDirectorBillingDetailsLoadingId, // refresh the breakdown when is present
        ]}
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false)
        }}
        variant={PaymentModalVariant.Licensing}
        actorKey={userDetails.actingAs as number}
        amountDue={{
          amount: lcdTotal?.totalDue.amount ?? '0.00',
          currencyCode: lcdTotal?.totalDue.currencyCode ?? 'USD',
        }}
        amountOwed={{
          amount: lcdTotal?.totalBalance.amount ?? '0.00',
          currencyCode: lcdTotal?.totalBalance.currencyCode ?? 'USD',
        }}
        programs={programs.map(
          ({
            programKey,
            program,
            directorOwes,
            directorDue,
            semesterOneStartDate,
          }) => ({
            programKey: Number(programKey),
            programName: program,
            amountOwed: directorOwes,
            amountDue: directorDue as CashAmount,
            semesterOneStartDate,
          })
        )}
        achDiscount={{
          amount: achDiscount,
          applyDiscountTimes: 0, // Don't apply for Licensing
        }}
      />
      <BasicModal
        isOpen={isAchNotAllowedModalOpen}
        maxWidth={'xs'}
        handleFormSubmit={(event: React.FormEvent<HTMLDivElement>) => {
          event.preventDefault()
          setIsAchNotAllowedModalOpen(false)
        }}
        dialogActions={
          <ActionButtons primaryButtonLabel={ContainedButtonVariant.Ok} />
        }
        dialogContent={
          <Box maxWidth={300} mx="auto" textAlign="center">
            <Typography component="p">
              {t(
                'LcdTotalOwedFetchingError.ConfirmationModal.Information.AchAllowed',
                'Please contact your country coordinator directly to make your payment or email accounting directly at accounting@classicalconversations.com.'
              )}
            </Typography>
          </Box>
        }
      />
      <Card>
        <CardFormHeader
          header={
            <Header
              id="LCDLicensingTableHeader"
              headerName={t('LCDHeader.CardFormHeader', 'Licensing')}
              component="h3"
              variant={HeaderVariant.Card}
            />
          }
          buttons={
            <Box display="flex" alignItems="center" sx={{ minWidth: 240 }}>
              <Box
                display="flex"
                alignItems="center"
                margin={theme.spacing(0, 3)}
                sx={{ color: colors.green.primary }}
                gap={7}
              >
                <Typography variant="h6" component="h5">
                  Total Balance: {totalBalance}
                </Typography>

                <Typography variant="h6" component="h5">
                  Total Due: {totalDue}
                </Typography>
              </Box>
              <Box margin={theme.spacing(0, 3)}>
                <ContainedButton
                  fullWidth
                  id="makeAPayment"
                  disabled={
                    !convertTwoDecimalStringToBig(
                      lcdTotal?.totalBalance.amount ?? '0.00'
                    ).gt(0)
                  }
                  variant={ContainedButtonVariant.MakeAPayment}
                  onClick={() => {
                    achAllowed
                      ? setIsPaymentModalOpen(true)
                      : setIsAchNotAllowedModalOpen(true)
                  }}
                />
              </Box>
            </Box>
          }
        />
      </Card>
      <Divider sx={{ borderBottomWidth: '3px', padding: '25px, 0, 25px, 0' }} />
    </>
  )
}

export default LCDHeader
